/**
 * Session reset — local escape hatch for when stale cookies / tokens
 * leave the SPA stuck (login succeeds but the UI doesn't proceed, or a
 * blank page sits forever after pulling new backend code). This has
 * happened twice in dev and is hard to recover from inside the app
 * because the auth init can wait on cookies the backend no longer
 * accepts.
 *
 * What this wipes (best-effort, never throws):
 *   - SuperTokens session state via Session.signOut() (network call with
 *     a tight timeout so it can't itself become the new stall)
 *   - Entra session + last-identity localStorage entries
 *   - Any auth-shaped localStorage keys (`st-*`, `jwt_token`,
 *     `eventhub.entra.*`)
 *   - Cookies readable from JS via document.cookie (HttpOnly cookies
 *     can't be cleared this way — the backend's signOut endpoint
 *     handles those when reachable)
 *
 * Used in three places:
 *   - LoginView's "Reset session" link (manual recovery)
 *   - main.ts on `?reset=1` boot (bookmark-style recovery, including the
 *     boot-watchdog overlay's "Reset session" button)
 *   - superTokensApiService.redirectToLogin (definitive auth rejection)
 *
 * Every caller names WHY (see sessionWipeReason.ts). The Entra bytes are
 * removed here directly rather than via entraAuthService so this module
 * stays import-light — so the wipe is reported here, once, when there
 * was an Entra session to lose.
 */
import Session from 'supertokens-web-js/recipe/session'
import { reportSessionWipe, type SessionWipeReason } from '@/utils/sessionWipeReason'

const ENTRA_KEYS = [
  'eventhub.entra.session',
  'eventhub.entra.state',
  'eventhub.entra.lastIdentity',
]

function clearLocalStorage(): void {
  try {
    localStorage.removeItem('jwt_token')
    for (const key of ENTRA_KEYS) localStorage.removeItem(key)
    // SuperTokens' web-js prefixes its keys with "st-". Sweep anything
    // matching so the next boot starts from a clean slate.
    const stKeys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith('st-')) stKeys.push(k)
    }
    for (const k of stKeys) localStorage.removeItem(k)
  } catch { /* private mode / quota — best-effort */ }
}

function clearJsCookies(): void {
  try {
    const cookies = document.cookie.split(';')
    for (const c of cookies) {
      const eq = c.indexOf('=')
      const name = (eq > -1 ? c.substring(0, eq) : c).trim()
      if (!name) continue
      // Expire on current path AND root path. Domain unset = current
      // host. Not all combos match every cookie, but the redundant
      // attempts are cheap and the unsuccessful ones are no-ops.
      const expire = 'Thu, 01 Jan 1970 00:00:00 GMT'
      document.cookie = `${name}=; expires=${expire}; path=/`
      document.cookie = `${name}=; expires=${expire}; path=${location.pathname}`
    }
  } catch { /* best-effort */ }
}

export async function resetAllSessionState(reason: SessionWipeReason = 'manual_reset'): Promise<void> {
  // Report BEFORE wiping — the context (had a session? how far from
  // expiry?) is only readable while the bytes are still there.
  try {
    const raw = localStorage.getItem('eventhub.entra.session')
    if (raw) {
      const parsed = JSON.parse(raw) as { expires_at?: number; refresh_token?: string }
      reportSessionWipe(reason, {
        ms_to_expiry: typeof parsed.expires_at === 'number' ? parsed.expires_at - Date.now() : null,
        had_refresh_token: !!parsed.refresh_token,
        full_reset: true,
      })
    }
  } catch { /* unreadable session blob — still reset */ }
  // Best-effort SuperTokens signout with a short timeout so a hung
  // backend can't make the recovery path itself stall.
  try {
    await Promise.race([
      Session.signOut(),
      new Promise<void>((resolve) => setTimeout(resolve, 1500)),
    ])
  } catch { /* swallow — we're tearing down anyway */ }
  clearLocalStorage()
  clearJsCookies()
}
